import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { config, easings, useSprings } from '@react-spring/web';
import { useLatest } from 'react-use';

import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useEffectOnChange } from '@sorare/core/src/hooks/state/useEffectOnChange.ts';
import { usePrevious } from '@sorare/core/src/hooks/usePrevious';
import { randomIntFromInterval } from '@sorare/core/src/lib/math';

import type { CardRevealUIState } from '#src/components/essence/CardPullExperience/CardsReveal/types.ts';

import type { useAnimatedCardStyles_cardPullCard } from './__generated__/useAnimatedCardStyles.graphql.ts';

const CARD_RATIO = [137, 220];
// Footer height (var(--quadruple-unit) = 32px) + margin-top (var(--half-unit) = 4px)
const CARD_FOOTER_HEIGHT = 36;

const getCardDimensions = ({
  rowsNb,
  colsNb,
  horizontalGap,
  verticalGap,
  containerWidth,
  containerHeight,
}: {
  rowsNb: number;
  colsNb: number;
  horizontalGap: number;
  verticalGap: number;
  containerWidth: number;
  containerHeight: number;
}) => {
  const maxCardWidth = Math.min(
    (containerWidth - horizontalGap * (colsNb - 1)) / colsNb,
    CARD_RATIO[0] * 1.7
  );
  // Subtract footer height for each row from the available container height
  const availableHeight = containerHeight - CARD_FOOTER_HEIGHT * rowsNb;
  const maxCardHeight = Math.min(
    (availableHeight - verticalGap * (rowsNb - 1)) / rowsNb,
    CARD_RATIO[1] * 1.7
  );
  let cardWidth = CARD_RATIO[0];
  let cardHeight = CARD_RATIO[1];
  if (maxCardWidth < (maxCardHeight * CARD_RATIO[0]) / CARD_RATIO[1]) {
    cardWidth = maxCardWidth;
    cardHeight = (cardWidth * CARD_RATIO[1]) / CARD_RATIO[0];
  } else {
    cardHeight = maxCardHeight;
    cardWidth = (cardHeight * CARD_RATIO[0]) / CARD_RATIO[1];
  }

  return {
    cardWidth,
    cardHeight,
    scale: cardWidth / CARD_RATIO[0],
    initialCardHeight: CARD_RATIO[1],
  };
};

const useColsNb = ({ fiftyFiftyRevealed }: { fiftyFiftyRevealed: boolean }) => {
  const isLaptopAndAbove = useIsLaptop();
  const mobileColsNb = fiftyFiftyRevealed ? 3 : 4;
  const colsNb = isLaptopAndAbove ? 5 : mobileColsNb;
  return colsNb;
};

export const useAnimatedCardStyles = ({
  uiState,
  cards,
  containerWidth,
  containerHeight,
  fiftyFiftyHaveDisappeared,
}: {
  uiState: CardRevealUIState;
  cards: useAnimatedCardStyles_cardPullCard[];
  containerWidth: number;
  containerHeight: number;
  fiftyFiftyHaveDisappeared: boolean;
}) => {
  const isLaptopAndAbove = useIsLaptop();

  const horizontalGap = isLaptopAndAbove ? 32 : 8;
  const verticalGap = isLaptopAndAbove ? 48 : 24;

  const fiftyFiftyRevealed =
    cards.some(card => card.fiftyFiftySelected) &&
    cards.some(card => !card.fiftyFiftySelected);

  const colsNb = useColsNb({
    fiftyFiftyRevealed,
  });

  const nbCards = fiftyFiftyRevealed
    ? cards.filter(card => card.fiftyFiftySelected).length
    : cards.length;
  const rowsNb = Math.ceil(nbCards / colsNb);

  const { cardWidth, cardHeight, scale } = getCardDimensions({
    rowsNb,
    colsNb,
    containerWidth,
    containerHeight,
    horizontalGap,
    verticalGap,
  });

  const getIdleState = (index: number) => {
    const position = index - nbCards / 2;
    const idleScale =
      containerHeight / cardHeight < 1.2 ? containerHeight / cardHeight : 1.2;
    return {
      // Fan out cards
      x: position * 80,
      y: Math.abs(position) * 15 - 40,
      rotate: `${position * 5}deg`,
      zIndex: 5 - Math.abs(position),
      '--animated-card-overlay-opacity': 1 - Math.abs(position) / 5,
      visibility: index === 0 ? 'hidden' : 'visible',
      scale: idleScale - Math.abs(position) / 15,
    };
  };

  const getPulledSettledState = (index: number) => {
    const visibleCards = cards.filter(
      card => card.fiftyFiftySelected !== false
    );
    const finalIndex = visibleCards.findIndex(
      card => card.id === cards[index].id
    );
    if (finalIndex === -1) {
      return {
        zIndex: 0,
        x: 0,
        y: 0,
        rotate: '0deg',
        scale,
      };
    }

    const unalignedCards = nbCards % colsNb;
    // center cards on last row
    const centeredIndex =
      finalIndex >= nbCards - unalignedCards
        ? finalIndex + (colsNb - unalignedCards) / 2
        : finalIndex;
    const colNo = centeredIndex % colsNb;
    const horizontalCenterOffset =
      ((colsNb - 1) / 2) * (cardWidth + horizontalGap);

    const rowNo = Math.floor(centeredIndex / colsNb);
    const verticalCenterOffset =
      (rowsNb === 2 ? 0.5 : Math.floor(rowsNb / 2)) *
      (cardHeight + verticalGap);

    return {
      zIndex: 0,
      x: colNo * (cardWidth + horizontalGap) - horizontalCenterOffset,
      y: rowNo * (cardHeight + verticalGap) - verticalCenterOffset,
      rotate: '0deg',
      '--animated-card-overlay-opacity': 1,
      visibility: 'visible',
      scale,
    };
  };

  const [styles, api] = useSprings(cards.length, index => {
    return {
      from: ['pulled', 'selected', 'pickRevealed', 'allRevealed'].includes(
        uiState
      )
        ? getPulledSettledState(index)
        : getIdleState(index),
    };
  });

  const previousContainerWidth = usePrevious(containerWidth);
  const previousContainerHeight = usePrevious(containerHeight);
  if (
    previousContainerWidth !== containerWidth ||
    previousContainerHeight !== containerHeight
  ) {
    api.start(index =>
      uiState === 'idle'
        ? { to: getIdleState(index), immediate: true }
        : {
            to: getPulledSettledState(index),
          }
    );
  }

  const previousFiftyFiftyHaveDisappeared = usePrevious(
    fiftyFiftyHaveDisappeared
  );
  if (
    previousFiftyFiftyHaveDisappeared !== fiftyFiftyHaveDisappeared &&
    fiftyFiftyHaveDisappeared
  ) {
    api.start(index => {
      return {
        to: getPulledSettledState(index),
        config: config.gentle,
      };
    });
  }

  const previousUIState = usePrevious(uiState);
  const latestUIState = useLatest(uiState);

  const startAnimation = () => {
    if (uiState === 'idle') {
      api.start(index => {
        return {
          to: getIdleState(index),
          immediate: true,
        };
      });
    } else if (uiState === 'pulling') {
      api.start(i => ({
        to: {
          x: 0,
          y: 0,
          '--animated-card-overlay-opacity': 1,
          rotate: '0deg',
          scale: 1.2,
          config: config.gentle,
        },
        onRest: () => {
          const startAnim = () => {
            // need to use latestUIState, uiState can be outdated due to stale closure
            if (latestUIState.current !== 'pulling') {
              return;
            }
            const stepLength = 15;
            api.start(index => {
              return {
                from: {
                  x: 0,
                  y: 0,
                  zIndex: 0,
                  rotate: '0deg',
                  config: config.gentle,
                },
                to: [
                  {
                    x:
                      randomIntFromInterval(0, 6) *
                      stepLength *
                      (randomIntFromInterval(0, 1) ? 1 : -1),
                    y:
                      randomIntFromInterval(0, 6) *
                      stepLength *
                      (randomIntFromInterval(0, 1) ? 1 : -1),
                    config: {
                      duration: 500,
                      easing: easings.linear,
                    },
                    delay: 1000,
                  },
                  {
                    x: 0,
                    y: 0,
                    rotate: '0deg',
                    config: {
                      duration: 500,
                      easing: easings.linear,
                    },
                    onRest: () => {
                      if (index === cards.length - 1) {
                        startAnim();
                      }
                    },
                  },
                ],
              };
            });
          };
          if (i === cards.length - 1) {
            startAnim();
          }
        },
      }));
    } else if (uiState === 'pulled') {
      // stop potential queued pulling animation
      // that can cause the cards to ends at center
      api.stop();
      if (previousUIState === 'allRevealed') {
        // snowglobe reshuffle
        api.start(index => ({
          to: [
            {
              x: 0,
              y: 0,
              rotate: '0deg',
              scale: 1.2,
              config: config.gentle,
            },
            {
              x:
                randomIntFromInterval(0, 6) *
                15 *
                (randomIntFromInterval(0, 1) ? 1 : -1),
              y:
                randomIntFromInterval(0, 6) *
                15 *
                (randomIntFromInterval(0, 1) ? 1 : -1),
              delay: 1000,
              config: {
                duration: 500,
                easing: easings.linear,
              },
            },
            {
              x: 0,
              y: 0,
              rotate: '0deg',
              config: {
                duration: 500,
                easing: easings.linear,
              },
            },
            {
              // Center cards
              x: 0,
              y: 0,
              rotate: '0deg',
              scale: 1.2,
              config: config.gentle,
            },
            {
              // scale down cards
              scale,
              config: config.stiff,
            },
            getPulledSettledState(index),
          ],
        }));
        return;
      }
      api.start(index => {
        return {
          to: [
            {
              // Center cards
              x: 0,
              y: 0,
              rotate: '0deg',
              scale: 1.2,
              config: config.gentle,
            },
            {
              // scale down cards
              scale,
              config: config.stiff,
            },
            getPulledSettledState(index),
          ],
        };
      });
    }
  };

  useEffectOnChange(uiState, () => {
    startAnimation();
  });

  return styles;
};

useAnimatedCardStyles.fragments = {
  cardPullCard: gql`
    fragment useAnimatedCardStyles_cardPullCard on CardPullCard {
      id
      fiftyFiftySelected
    }
  ` as TypedDocumentNode<useAnimatedCardStyles_cardPullCard>,
};
